import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { activeBackendIssueStatuses } from './constants';
import { isAiCandidateCurationRunActive, isAiIssueTriageRunActive } from './helpers';
import type { ObjectiveMode } from '../parameterDiscovery/ObjectiveModeControl';
import type { AuditMetricScope, ParameterDiscoveryMetric, ScorecardGenerationExternalTool } from './types';

interface UseParameterDiscoveryAuditQueriesParams {
    enabled: boolean;
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    metrics: ParameterDiscoveryMetric[];
    scorecardMetric: ParameterDiscoveryMetric;
    objectiveMode: ObjectiveMode;
    objectiveEvidenceReady: boolean;
    realizedOutcomeSetId: number | null;
    auditScope: AuditMetricScope;
    scorecardGenerationTool: ScorecardGenerationExternalTool;
    selectedFeatureAuditSessionId: number | null;
    selectedBackendRepairSessionId: number | null;
    selectedGenerationSessionId: number | null;
}

export const useParameterDiscoveryAuditQueries = ({
    enabled,
    experimentId,
    duration,
    horizonSeconds,
    metrics,
    scorecardMetric,
    objectiveMode,
    objectiveEvidenceReady,
    realizedOutcomeSetId,
    auditScope,
    scorecardGenerationTool,
    selectedFeatureAuditSessionId,
    selectedBackendRepairSessionId,
    selectedGenerationSessionId
}: UseParameterDiscoveryAuditQueriesParams) => {
    const queryClient = useQueryClient();

    const sourceReportInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            metrics,
            familyLimit: 20
        }),
        [duration, experimentId, horizonSeconds, metrics]
    );

    const scorecardDraftInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            metric: scorecardMetric,
            ...(objectiveMode === 'strategy_exit' ? { objectiveMode, realizedOutcomeSetId } : {}),
            limit: 200,
            ruleBudget: 12,
            requireCurated: true,
            includeMaybe: true,
            includeWeak: true,
            includeSuspectAccuracy: false,
            requirePositiveBestGroupCiLower: true
        }),
        [duration, experimentId, horizonSeconds, scorecardMetric, objectiveMode, realizedOutcomeSetId]
    );

    const issueMetric = auditScope === 'both' ? undefined : auditScope;

    const backendIssuesInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            ...(issueMetric ? { metric: issueMetric } : {}),
            statuses: activeBackendIssueStatuses,
            limit: 10
        }),
        [duration, experimentId, horizonSeconds, issueMetric]
    );

    const scorecardGenerationSessionsInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            metric: scorecardMetric,
            objectiveMode,
            realizedOutcomeSetId,
            limit: 5
        }),
        [duration, experimentId, horizonSeconds, scorecardMetric, objectiveMode, realizedOutcomeSetId]
    );

    const featureAuditSessionsInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            limit: 5
        }),
        [duration, experimentId, horizonSeconds]
    );

    const backendRepairSessionsInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            limit: 5
        }),
        [duration, experimentId, horizonSeconds]
    );

    const aiIssueTriageRunInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            metrics,
            batchSize: 25,
            batchIssueLimit: 100
        }),
        [duration, experimentId, horizonSeconds, metrics]
    );

    const aiCandidateCurationRunInput = useMemo(
        () => ({
            experimentId,
            duration,
            horizonSeconds,
            metrics,
            batchSize: 25,
            batchFeatureLimit: 100
        }),
        [duration, experimentId, horizonSeconds, metrics]
    );

    const aiIssueTriageRunQuery = useQuery({
        ...trpc.experiment.getAiIssueTriageRun.queryOptions(aiIssueTriageRunInput),
        enabled,
        refetchInterval: 5000
    });

    const aiBackendTriageRunning = isAiIssueTriageRunActive(aiIssueTriageRunQuery.data?.run);

    const aiCandidateCurationRunQuery = useQuery({
        ...trpc.experiment.getAiCandidateCurationRun.queryOptions(aiCandidateCurationRunInput),
        enabled,
        refetchInterval: 5000
    });

    const aiCandidateCurationRunning = isAiCandidateCurationRunActive(aiCandidateCurationRunQuery.data?.run);

    const sourceReportQuery = useQuery({
        ...trpc.experiment.getSourceAuditReport.queryOptions(sourceReportInput),
        enabled,
        refetchInterval: aiBackendTriageRunning || aiCandidateCurationRunning ? 5000 : false
    });

    const scorecardDraftQuery = useQuery({
        ...trpc.experiment.getScorecardDraft.queryOptions(scorecardDraftInput),
        enabled: enabled && objectiveEvidenceReady,
        refetchInterval: aiCandidateCurationRunning ? 5000 : false,
        staleTime: 60000
    });

    const backendIssuesQuery = useQuery({
        ...trpc.experiment.listBackendIssues.queryOptions(backendIssuesInput),
        enabled,
        refetchInterval: aiBackendTriageRunning ? 5000 : false,
        staleTime: 60000
    });

    const scorecardGenerationSessionsQuery = useQuery({
        ...trpc.experiment.listScorecardGenerationSessions.queryOptions(scorecardGenerationSessionsInput),
        enabled: enabled && (objectiveMode === 'fixed_horizon' || realizedOutcomeSetId !== null),
        refetchInterval: 5000,
        staleTime: 15000
    });

    const featureAuditSessionsQuery = useQuery({
        ...trpc.experiment.listParameterDiscoveryFeatureAuditSessions.queryOptions(featureAuditSessionsInput),
        enabled,
        refetchInterval: 5000,
        staleTime: 15000
    });

    const backendRepairSessionsQuery = useQuery({
        ...trpc.experiment.listBackendFeatureRepairSessions.queryOptions(backendRepairSessionsInput),
        enabled,
        refetchInterval: 5000,
        staleTime: 15000
    });

    const mcpClientReadinessQuery = useQuery({
        ...trpc.user.listMcpClientReadiness.queryOptions(),
        enabled,
        refetchInterval: 15000,
        staleTime: 10000
    });

    const selectedMcpReadiness = mcpClientReadinessQuery.data?.clients.find((client) => client.externalTool === scorecardGenerationTool);

    const featureAuditSessions = useMemo(() => {
        const sessions = featureAuditSessionsQuery.data ?? [];

        return sessions.filter((session) => {
            if (auditScope === 'both') return session.metric === null;
            return session.metric === auditScope;
        });
    }, [auditScope, featureAuditSessionsQuery.data]);

    const scorecardGenerationSessions = scorecardGenerationSessionsQuery.data ?? [];
    const activeFeatureAuditSessionId = selectedFeatureAuditSessionId ?? featureAuditSessions[0]?.id ?? null;
    const activeBackendRepairSessionId = selectedBackendRepairSessionId ?? backendRepairSessionsQuery.data?.[0]?.id ?? null;

    const activeGenerationSessionId =
        scorecardGenerationSessions.some((session) => session.id === selectedGenerationSessionId) && selectedGenerationSessionId != null
            ? selectedGenerationSessionId
            : (scorecardGenerationSessions[0]?.id ?? null);

    const featureAuditSessionQuery = useQuery({
        ...trpc.experiment.getParameterDiscoveryFeatureAuditSession.queryOptions({
            sessionId: activeFeatureAuditSessionId ?? 1,
            externalTool: scorecardGenerationTool
        }),
        enabled: enabled && activeFeatureAuditSessionId != null,
        refetchInterval: 5000,
        staleTime: 5000
    });

    const scorecardGenerationSessionQuery = useQuery({
        ...trpc.experiment.getScorecardGenerationSession.queryOptions({ sessionId: activeGenerationSessionId ?? 1 }),
        enabled: enabled && activeGenerationSessionId != null,
        refetchInterval: 5000,
        staleTime: 5000
    });

    const backendRepairSessionQuery = useQuery({
        ...trpc.experiment.getBackendFeatureRepairSession.queryOptions({
            sessionId: activeBackendRepairSessionId ?? 1,
            externalTool: scorecardGenerationTool
        }),
        enabled: enabled && activeBackendRepairSessionId != null,
        refetchInterval: 5000,
        staleTime: 5000
    });

    const refreshAuditQueries = () => {
        void queryClient.invalidateQueries({ queryKey: trpc.experiment.getSourceAuditReport.queryOptions(sourceReportInput).queryKey });
        void queryClient.invalidateQueries({ queryKey: trpc.experiment.getScorecardDraft.queryOptions(scorecardDraftInput).queryKey });

        void queryClient.invalidateQueries({ queryKey: trpc.experiment.listBackendIssues.queryOptions(backendIssuesInput).queryKey });
        void queryClient.invalidateQueries({ queryKey: trpc.experiment.getAiIssueTriageRun.queryOptions(aiIssueTriageRunInput).queryKey });

        void queryClient.invalidateQueries({
            queryKey: trpc.experiment.getAiCandidateCurationRun.queryOptions(aiCandidateCurationRunInput).queryKey
        });

        void queryClient.invalidateQueries({
            queryKey: trpc.experiment.listScorecardGenerationSessions.queryOptions(scorecardGenerationSessionsInput).queryKey
        });

        void queryClient.invalidateQueries({
            queryKey: trpc.experiment.listParameterDiscoveryFeatureAuditSessions.queryOptions(featureAuditSessionsInput).queryKey
        });

        void queryClient.invalidateQueries({
            queryKey: trpc.experiment.listBackendFeatureRepairSessions.queryOptions(backendRepairSessionsInput).queryKey
        });

        if (activeFeatureAuditSessionId != null) {
            void queryClient.invalidateQueries({
                queryKey: trpc.experiment.getParameterDiscoveryFeatureAuditSession.queryOptions({
                    sessionId: activeFeatureAuditSessionId,
                    externalTool: scorecardGenerationTool
                }).queryKey
            });
        }

        if (activeBackendRepairSessionId != null) {
            void queryClient.invalidateQueries({
                queryKey: trpc.experiment.getBackendFeatureRepairSession.queryOptions({
                    sessionId: activeBackendRepairSessionId,
                    externalTool: scorecardGenerationTool
                }).queryKey
            });
        }

        if (activeGenerationSessionId != null) {
            void queryClient.invalidateQueries({
                queryKey: trpc.experiment.getScorecardGenerationSession.queryOptions({ sessionId: activeGenerationSessionId }).queryKey
            });
        }

        void queryClient.invalidateQueries({ queryKey: trpc.user.listMcpClientReadiness.queryOptions().queryKey });
    };

    return {
        sourceReportInput,
        aiIssueTriageRunInput,
        aiCandidateCurationRunInput,
        aiIssueTriageRunQuery,
        aiCandidateCurationRunQuery,
        aiBackendTriageRunning,
        aiCandidateCurationRunning,
        sourceReportQuery,
        scorecardDraftQuery,
        backendIssuesQuery,
        scorecardGenerationSessionsQuery,
        featureAuditSessionsQuery,
        backendRepairSessionsQuery,
        selectedMcpReadiness,
        featureAuditSessions,
        scorecardGenerationSessions,
        activeFeatureAuditSessionId,
        activeBackendRepairSessionId,
        activeGenerationSessionId,
        featureAuditSessionQuery,
        scorecardGenerationSessionQuery,
        backendRepairSessionQuery,
        refreshAuditQueries
    };
};
